import type { LeadsSummaryResponse } from '@/api/types';
import { formatDuration } from './format';
import { SEQ_RAMP } from './viz';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * When leads arrive, and how fast they were answered.
 *
 * The two belong on one card because they answer one question together: staffing. An arrival peak
 * at 21:00 only matters next to what the response time does at 21:00.
 *
 * <b>Median beside mean, always.</b> One chat answered after a weekend moves the mean past
 * anything an operator would recognise, and a mean alone is how "we reply in 4 hours" becomes an
 * unchallenged fact. Both are shown, and the median leads.
 */
export function LeadTiming({ summary }: { summary: LeadsSummaryResponse }) {
  const hasChatTiming =
    summary.median_first_response_seconds !== null || summary.median_handling_seconds !== null;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Timing</h2>
        <span className="t-label subtle">chat leads only</span>
      </div>

      <div className="card-body">
        {hasChatTiming ? (
          <div className="lead-timing-grid">
            <Metric
              label="First reply"
              median={summary.median_first_response_seconds}
              mean={summary.avg_first_response_seconds}
              hint="visitor's first message → first human reply"
            />
            <Metric
              label="Conversation length"
              median={summary.median_handling_seconds}
              mean={summary.avg_handling_seconds}
              hint="start → close; open chats excluded"
            />
            <Metric
              label="Queue wait"
              median={null}
              mean={summary.avg_queue_seconds}
              hint="time before an agent picked it up"
            />
            <div className="lead-timing-metric">
              <div className="t-label muted">Missed chats</div>
              <div className="t-heading" style={{ color: summary.missed_count > 0 ? 'var(--warning)' : undefined }}>
                {summary.missed_count.toLocaleString('en-US')}
              </div>
              <div className="t-caption subtle">
                {summary.avg_csat !== null
                  ? `aged out unanswered · CSAT ${summary.avg_csat.toFixed(1)}/5`
                  : 'aged out unanswered'}
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state t-body">
            No chat timings in range — these come from widget conversations.
          </div>
        )}

        <Histogram
          title="Arrivals by hour (UTC)"
          buckets={summary.hour_histogram}
          labelFor={(i) => `${String(i).padStart(2, '0')}`}
          emphasise={(i) => i % 6 === 0}
        />

        <Histogram
          title="Arrivals by weekday"
          buckets={summary.weekday_histogram}
          labelFor={(i) => WEEKDAYS[i] ?? String(i)}
          emphasise={() => true}
        />
      </div>
    </div>
  );
}

function Metric({
  label, median, mean, hint,
}: { label: string; median: number | null; mean: number | null; hint: string }) {
  const headline = median ?? mean;

  return (
    <div className="lead-timing-metric">
      <div className="t-label muted">{label}</div>
      <div className="t-heading">{formatDuration(headline)}</div>
      <div className="t-caption subtle">
        {median !== null && mean !== null
          ? `median · mean ${formatDuration(mean)}`
          : median !== null
            ? 'median'
            : mean !== null
              ? 'mean'
              : hint}
      </div>
      {(median !== null || mean !== null) && <div className="t-caption subtle">{hint}</div>}
    </div>
  );
}

/**
 * Small ordered-bucket chart.
 *
 * A column chart rather than a bar list because the categories are ORDERED (hours, weekdays) and
 * their sequence carries the meaning — sorting these by magnitude would destroy the shape that
 * makes them worth plotting. One hue from the shared sequential ramp; the ramp is legal here for
 * the same reason.
 */
function Histogram({
  title, buckets, labelFor, emphasise,
}: {
  title: string;
  buckets: number[];
  labelFor: (index: number) => string;
  emphasise: (index: number) => boolean;
}) {
  const max = Math.max(...buckets, 1);
  const total = buckets.reduce((sum, value) => sum + value, 0);

  return (
    <div className="lead-histogram">
      <div className="t-label muted lead-histogram-title">{title}</div>

      {total === 0 ? (
        <div className="t-caption subtle">Nothing captured in this range.</div>
      ) : (
        <div className="lead-histogram-bars">
          {buckets.map((value, index) => (
            <div className="lead-histogram-col" key={index}>
              {/* The value rides on the title so the chart stays free of per-bar numbers, which
                  at 24 buckets would be unreadable ink. */}
              <div
                className="lead-histogram-bar"
                style={{
                  height: `${Math.max(2, (value / max) * 100)}%`,
                  background: value === max ? SEQ_RAMP[3] : SEQ_RAMP[1],
                }}
                title={`${labelFor(index)}: ${value.toLocaleString('en-US')} leads`}
              />
              <div className="lead-histogram-label t-caption subtle">
                {emphasise(index) ? labelFor(index) : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
